import AppSettings from "@/appsettings";
import { AUTH_SESSION_KEYS } from "@/constants/authFlowKeys";
import { registerAuthFailureHandler } from "@/queryClient";
import { createCookie } from "@/screens/utils/cookie";
import { postMessageToApp } from "@/screens/utils/hybridAppHelpers";
import {
  clearIncompleteAuthSession,
  clearStaleSessionCookie,
  resetLogoutBrowserState,
} from "./authCleanupService";
import {
  AUTH_EVENTS,
  AUTH_STATES,
  createAuthStateMachine,
  EXTERNAL_STATUS,
} from "./authStateMachine";
import { getCurrentUser } from "./currentUser";
import { consumeDesiredNavRedirect, getDesiredNav } from "./desiredNavService";
import { markDocumentAuthenticated } from "./documentAuthHistory";
import {
  IDLE_LOGOUT_MESSAGE,
  markSessionExpired,
  setLoggedOutMessage,
} from "./loggedOutMessageService";
import { renewExpiredSession } from "./oidcManagerService";
import { getAuthProvider } from "./providers";
import { resetAuthenticatedSessionBootstrap } from "./sessionBootstrapOrchestrator";
import { hydrateUserManagerFromStorage } from "./sessionHydrationService";
import { LOGIN_MARKS, markLoginEvent } from "../perf/loginTimeline";

// Callback settle retry: 250ms × 12 = 3s max wait
const SETTLE_RETRY_MS = 250;
const SETTLE_MAX_ATTEMPTS = 12;

// Returns true if the storage event affects OIDC keys or raw token entries.
// Only match user/token keys, not transient oidc.state:* entries that
// are removed during signinRedirectCallback before the user is stored.
const isAuthStorageEvent = (event) => {
  if (event.storageArea !== localStorage) {
    return false;
  }
  if (event.key === null) {
    return true;
  }
  return (
    event.key.startsWith("oidc.user:") ||
    ["access_token", "id_token", "refresh_token", "expires_at"].includes(
      event.key,
    )
  );
};

// Returns true if localStorage contains OIDC user keys or raw token entries.
// Used during cross-tab detection: if the in-memory user is valid but storage
// is empty, another tab cleared the session.
const hasSharedAuthStorage = () => {
  const keys = Array.from({ length: localStorage.length }, (_, i) =>
    localStorage.key(i),
  );
  return (
    keys.some((key) => key?.startsWith("oidc.user:")) ||
    ["access_token", "id_token", "refresh_token", "expires_at"].every(
      (key) => !!localStorage.getItem(key),
    )
  );
};

// One sign-in session: the auth state machine, the evaluation that drives it
// from stored tokens, and the UserManager and browser events that re-run that
// evaluation. Plain JavaScript, so it can exist and settle before React renders
// anything; AuthSessionProvider exposes it to components.
//
//   attach()        registers the QueryClient's auth-failure handler and the
//                   first-decision perf mark. Returns a detach function.
//   start(manager)  hands over the UserManager: MANAGER_READY, the first
//                   evaluation, then event wiring. Returns a stop function.
//   setNavigate(fn) how the session navigates (restoring the deep link).
export const createAuthSession = ({ navigate } = {}) => {
  const navigateRef = { current: navigate };
  let userManager = null;
  const userManagerRef = {
    get current() {
      return userManager;
    },
  };

  const machine = createAuthStateMachine({
    effects: {
      // Fired on every AUTHENTICATED entry. Clears the callback settle
      // flag, restores the pre-login URL, publishes the askEngage cookie,
      // and posts UserDetails to the native app (if running in a WebView).
      onAuthenticated: async () => {
        markDocumentAuthenticated();
        sessionStorage.removeItem("postCallbackAuthPending");
        consumeDesiredNavRedirect(AppSettings, navigateRef.current);

        const { regionCode, dealerCode, jobCode } = getCurrentUser();
        createCookie(
          "askEngage",
          JSON.stringify({
            rc: regionCode,
            dc: dealerCode,
            jc: jobCode,
          }),
          0.5,
          true,
          true,
        );

        try {
          const user = await userManagerRef.current?.getUser();
          if (user?.profile) {
            postMessageToApp("UserDetails", user.profile);
          }
        } catch (error) {
          console.error("Unable to publish authenticated user details.", error);
        }
      },

      // Token expired while the user was authenticated. Cleans up
      // browser state and sets the "session expired" banner flag so
      // LoginService can forward it to the login page via a URL param.
      onSessionExpired: () => {
        try {
          markSessionExpired();

          const isLocalHost = globalThis.location?.hostname === "localhost";
          const previousUrl = getDesiredNav();

          resetAuthenticatedSessionBootstrap();
          clearStaleSessionCookie({ isLocalHost });
          resetLogoutBrowserState({
            desiredNav: previousUrl,
            isLocalHost,
          });
          setLoggedOutMessage(IDLE_LOGOUT_MESSAGE);
        } catch (error) {
          console.error("Session expiry cleanup failed:", error);
        }
      },

      // Another tab cleared auth storage. Drops the stale in-memory OIDC
      // user so this tab doesn't keep serving an expired session.
      onCrossTabLogout: async () => {
        try {
          await clearIncompleteAuthSession({
            userManager: userManagerRef.current,
          });
        } catch (error) {
          console.error("Cross-tab logout cleanup failed:", error);
        }
      },

      // Unrecoverable error during hydration or evaluation.
      onError: async () => {
        try {
          await clearIncompleteAuthSession({
            userManager: userManagerRef.current,
          });
        } catch (error) {
          console.error("Error cleanup failed:", error);
        }
      },
    },
  });

  // Hydrates OIDC state and dispatches the appropriate machine event.
  //
  // wasAuthenticated — true when re-evaluating from the AUTHENTICATED state.
  //   Distinguishes "first hydration" from "re-check after being
  //   authenticated" (both transit through HYDRATING).
  // validateSharedStorage — true for storage/focus events where cross-tab
  //   logout needs to be detected.
  // settleAttempt — tracks callback settle retry count (max 12 × 250ms = 3s).
  const evaluate = async ({
    validateSharedStorage = false,
    settleAttempt = 0,
    wasAuthenticated = false,
  } = {}) => {
    if (!userManager) {
      return;
    }

    let hydrationFailed = false;

    try {
      await hydrateUserManagerFromStorage({
        userManager,
        onHydrationFailure: () => {
          hydrationFailed = true;
          machine.send(AUTH_EVENTS.HYDRATION_FAILED);
        },
      });

      if (hydrationFailed) {
        return;
      }

      const user = await userManager.getUser();

      // Another tab signed out, or its session ended: shared storage no longer
      // holds a user. Clean up only this tab's own auth state. The full wipe
      // of a session expiry would race the other tab's writes -- localStorage
      // is only eventually consistent across tab processes -- and can erase
      // what it just stored, such as the post-logout account-picker flag.
      if (validateSharedStorage && wasAuthenticated && !user) {
        machine.send(AUTH_EVENTS.CROSS_TAB_LOGOUT);
        return;
      }

      if (user?.expired === false) {
        if (validateSharedStorage && !hasSharedAuthStorage()) {
          machine.send(AUTH_EVENTS.CROSS_TAB_LOGOUT);
          return;
        }
        machine.send(AUTH_EVENTS.TOKEN_VALID);
        return;
      }

      // No valid token — check if a callback is still settling
      const callbackPending =
        sessionStorage.getItem(AUTH_SESSION_KEYS.POST_CALLBACK_AUTH_PENDING) ===
        "true";

      if (callbackPending && settleAttempt < SETTLE_MAX_ATTEMPTS) {
        machine.send(AUTH_EVENTS.CALLBACK_PENDING);
        // Retry after a brief delay to let the callback settle
        globalThis.setTimeout(() => {
          evaluate({
            validateSharedStorage,
            settleAttempt: settleAttempt + 1,
          });
        }, SETTLE_RETRY_MS);
        return;
      }

      // Clear the pending flag if settle attempts are exhausted
      if (callbackPending) {
        sessionStorage.removeItem(AUTH_SESSION_KEYS.POST_CALLBACK_AUTH_PENDING);
        machine.send(AUTH_EVENTS.SETTLE_TIMEOUT);
        return;
      }

      // A returning session can outlive its tokens by its refresh token (up
      // to 24h under Entra), so use it before sending the user back through
      // the provider. See renewsExpiredSessions.
      if (
        user?.expired === true &&
        user.refresh_token &&
        !wasAuthenticated &&
        !globalThis.ReactNativeWebView &&
        getAuthProvider().renewsExpiredSessions
      ) {
        if (await renewExpiredSession(userManager)) {
          machine.send(AUTH_EVENTS.TOKEN_VALID);
          return;
        }
      }

      // Determine whether this was a session expiry or a fresh visit.
      // wasAuthenticated is true when re-evaluating from the AUTHENTICATED
      // state (TOKEN_EXPIRED_WAS_AUTHED triggers cleanup). On initial
      // hydration it is false (just TOKEN_EXPIRED, no cleanup).
      if (wasAuthenticated && machine.getState() !== AUTH_STATES.LOGGING_OUT) {
        machine.send(AUTH_EVENTS.TOKEN_EXPIRED_WAS_AUTHED);
      } else {
        machine.send(AUTH_EVENTS.TOKEN_EXPIRED);
      }
    } catch (error) {
      console.error("Unable to evaluate auth session.", error);
      machine.send(AUTH_EVENTS.ERROR);
    }
  };

  const attach = () => {
    const handleAuthFailure = () => {
      if (!globalThis.ReactNativeWebView) {
        machine.send(AUTH_EVENTS.TOKEN_EXPIRED_WAS_AUTHED);
      }
    };

    registerAuthFailureHandler(handleAuthFailure);
    const unsubscribe = machine.subscribe((newStatus) => {
      // Local/dev only, and once per document: the first status that is not
      // LOADING is the point this boot learned whether a session exists. On a
      // cold login the answer is "no", and everything before it was spent
      // finding that out. See perf/loginTimeline.
      if (newStatus !== EXTERNAL_STATUS.LOADING) {
        markLoginEvent(LOGIN_MARKS.AUTH_DECISION, { once: true });
      }
    });

    return () => {
      unsubscribe();
      registerAuthFailureHandler(null);
    };
  };

  // Initial evaluation + event wiring
  const start = (instance) => {
    userManager = instance;

    if (!userManager) {
      return;
    }

    machine.send(AUTH_EVENTS.MANAGER_READY);
    evaluate();

    // In the mobile WebView, the native app owns the full auth lifecycle:
    // token acquisition, refresh, and logout. The SPA is a passive consumer
    // of tokens injected into localStorage. Skip all event listeners that
    // would cause re-evaluation — signinSilent fails in the WebView iframe
    // sandbox, storage/focus events fire spuriously, and accessTokenExpired
    // would push the machine to UNAUTHENTICATED and trigger a redirect loop
    // with the native token re-injection system.
    if (globalThis.ReactNativeWebView) {
      return;
    }

    const authEvents = userManager.events;
    if (!authEvents) {
      return;
    }

    // All event handlers capture wasAuthenticated BEFORE sending RE_EVALUATE
    // (which moves the machine to HYDRATING). This lets evaluate() correctly
    // distinguish session expiry (was authed → expired) from initial load.

    const handleSessionChange = () => {
      const wasAuthenticated = machine.getState() === AUTH_STATES.AUTHENTICATED;
      if (wasAuthenticated) {
        machine.send(AUTH_EVENTS.RE_EVALUATE);
      }
      evaluate({ wasAuthenticated });
    };

    // For a provider that renewsExpiredSessions, an expired access token is
    // not an expired session, and the expiring-timer renewal can fail on a
    // flaky network or be cut off by a sleeping laptop. Renew while still
    // AUTHENTICATED so the page stays mounted; success fires userLoaded, which
    // re-evaluates with the fresh user.
    const handleTokenExpired = async () => {
      if (
        machine.getState() === AUTH_STATES.AUTHENTICATED &&
        getAuthProvider().renewsExpiredSessions
      ) {
        const user = await userManager.getUser().catch(() => null);
        if (user?.refresh_token && (await renewExpiredSession(userManager))) {
          return;
        }
      }
      handleSessionChange();
    };

    // Cross-tab auth changes (login in another tab, logout in another tab)
    // are detected via the storage event, which only fires for changes made
    // by *other* tabs. This avoids the disruptive re-evaluation that
    // pageshow/focus/visibilitychange listeners caused on every tab switch.
    const handleStorageChange = (event) => {
      if (isAuthStorageEvent(event)) {
        const wasAuthenticated =
          machine.getState() === AUTH_STATES.AUTHENTICATED;
        if (wasAuthenticated) {
          machine.send(AUTH_EVENTS.RE_EVALUATE);
        }
        evaluate({ validateSharedStorage: true, wasAuthenticated });
      }
    };

    authEvents.addUserLoaded(handleSessionChange);
    authEvents.addUserUnloaded(handleSessionChange);
    authEvents.addAccessTokenExpired(handleTokenExpired);
    globalThis.addEventListener("storage", handleStorageChange);

    return () => {
      authEvents.removeUserLoaded(handleSessionChange);
      authEvents.removeUserUnloaded(handleSessionChange);
      authEvents.removeAccessTokenExpired(handleTokenExpired);
      globalThis.removeEventListener("storage", handleStorageChange);
    };
  };

  return {
    machine,
    getStatus: machine.getStatus,
    subscribe: machine.subscribe,
    attach,
    start,
    setNavigate: (fn) => {
      navigateRef.current = fn;
    },
  };
};
